import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { GoogleOAuthProvider } from "@react-oauth/google";
import NotFound from "@/pages/not-found";

import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { AppLayout } from "@/components/layout/app-layout";

// Pages
import Landing from "@/pages/landing";
import Login from "@/pages/login";
import Register from "@/pages/register";
import VerifyEmail from "@/pages/verify-email";
import Dashboard from "@/pages/dashboard";
import NewSession from "@/pages/sessions/new";
import SessionDetail from "@/pages/sessions/detail";
import History from "@/pages/history";
import Settings from "@/pages/settings";
import Space from "@/pages/space";
import Privacy from "@/pages/legal/privacy";
import Terms from "@/pages/legal/terms";
import Refund from "@/pages/legal/refund";
import Cookies from "@/pages/legal/cookies";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Wrapper
function ProtectedRoute({ component: Component, ...rest }: { component: any, path: string }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  
  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  return (
    <Route {...rest}>
      <AppLayout>
        <Component />
      </AppLayout>
    </Route>
  );
}

// Public Route Wrapper (redirects to dashboard if logged in)
function PublicRoute({ component: Component, ...rest }: { component: any, path: string }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <Route {...rest}>
      <Component />
    </Route>
  );
}

function Router() {
  return (
    <Switch>
      <PublicRoute path="/" component={Landing} />
      <PublicRoute path="/login" component={Login} />
      <PublicRoute path="/register" component={Register} />
      <Route path="/verify-email" component={VerifyEmail} />
      
      <ProtectedRoute path="/dashboard" component={Dashboard} />
      <ProtectedRoute path="/sessions/new" component={NewSession} />
      <ProtectedRoute path="/sessions/:id" component={SessionDetail} />
      <ProtectedRoute path="/history" component={History} />
      <ProtectedRoute path="/space" component={Space} />
      <ProtectedRoute path="/settings" component={Settings} />
      
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/refund" component={Refund} />
      <Route path="/cookies" component={Cookies} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <AuthProvider>
                <Router />
              </AuthProvider>
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
